<template>
  <div class="content">
    <PageHeader title="AKBS申請一覧">
      <template #title-header-content>
        <div class="flex-1 flex items-center justify-end mr-2">
          <!-- 左右の一覧をまとめて取り直す -->
          <Icon
            iconName="Refresh"
            :clickable="false"
            :class="{ 'animate-spin': staff.loading || contract.loading }"
            class="ml-4 cursor-pointer text-white hover:text-gray-300"
            @click.native="getAll()"
          />
        </div>
      </template>
    </PageHeader>

    <!-- 左：雇用指示の申請（清掃員の申請 req_akbs）、右：契約詳細の申請（req_akbs_contract）
         申請の種類ごとに、絞り込み・件数・一覧・ページネーションを別々に持つ。絞り込みは各パネルの見出しに置く
         見出しは左右とも「1段目：名前と件数、2段目：絞り込み」に揃える（ユーザー 2026-09-22） -->
    <main class="absolute top-20 -mt-1 bottom-4 w-screen">
      <div class="h-full px-4 flex gap-4">

        <!-- ========== 左：雇用指示の申請（清掃員の申請） ========== -->
        <section class="panel">
          <div class="panel-head">
            <div class="panel-title">
              <Icon iconName="UserGroup" class="w-5 h-5 mr-1" />
              雇用指示の申請
              <span class="panel-count">{{ staff.records }} 件</span>
            </div>
            <!-- 絞り込み（清掃員名・種別・作成者・状況）
                 ラベルは置かず、placeholder で何の欄か分かるようにする。高さは全部 filter-input で揃える
                 何かで絞っている欄は黄色にして分かるようにする（「全て」は絞っていないので色を付けない） -->
            <div class="panel-filter">
              <input
                v-model="staff.filter.staff"
                type="search"
                placeholder="清掃員名 検索"
                class="filter-input w-36 rounded border border-gray-300 px-2 text-sm"
                :class="staff.filter.staff ? 'filled' : ''"
                @keydown.enter="getStaffList(1)"
              />

              <!-- 種別（値は req_akbs.type の数値。空は全て） -->
              <select v-model="staff.filter.type" class="filter-input rounded border-gray-300 text-sm" :class="staff.filter.type ? 'filled' : ''" title="種別">
                <option value="">種別：全て</option>
                <option v-for="type in staffTypeOptions" :key="type.code" :value="type.code">
                  {{ type.name }}
                </option>
              </select>

              <input
                v-model="staff.filter.creator"
                type="search"
                placeholder="作成者 検索"
                class="filter-input w-28 rounded border border-gray-300 px-2 text-sm"
                :class="staff.filter.creator ? 'filled' : ''"
                @keydown.enter="getStaffList(1)"
              />

              <!-- 状況（「全て」は種別と同じく「状況：全て」と出す。値はバックが見る '全て' のまま） -->
              <select v-model="staff.filter.status" class="filter-input rounded border-gray-300 text-sm" :class="staff.filter.status !== statusAll ? 'filled' : ''" title="状況">
                <option v-for="status in staffStatusOptions" :key="status" :value="status">
                  {{ status === statusAll ? '状況：全て' : status }}
                </option>
              </select>

              <!-- 検索は1ページ目から。件数は絞り込み後の全件（ページの中の件数ではない） -->
              <PrimaryButton text="検索" size="sm" class="w-20" @click="getStaffList(1)" />
            </div>
          </div>

          <div class="list-box">
            <div v-if="staff.loading" class="flex h-full items-center justify-center font-bold text-gray-400">
              Searching...
            </div>

            <div v-else-if="!staff.list.length" class="flex h-full items-center justify-center font-bold text-gray-400">
              対象データがありません。
            </div>

            <!-- 幅は中身に合わせ、文字は折り返さない -->
            <table v-else class="w-full border-collapse text-sm whitespace-nowrap">
              <thead class="sticky top-0 bg-gray-100">
                <!-- 左右の一覧を横に並べるので、列を減らして2段にまとめる（種別の下に入退社日、作成者の下に承認者、状況の下に申請日／承認日）。ユーザー 2026-09-22 -->
                <tr class="border-b border-gray-300 text-left text-xs text-gray-600">
                  <th class="px-3 py-2">問い合わせNo</th>
                  <th class="px-3 py-2">種別</th>
                  <th class="px-3 py-2">清掃員名</th>
                  <th class="px-3 py-2">作成 / 承認</th>
                  <th class="px-3 py-2 text-center">状況</th>
                  <th class="px-3 py-2 w-20 text-center">編集</th>
                  <th class="px-3 py-2 w-20 text-center">承認</th>
                </tr>
              </thead>
              <tbody class="text-gray-700">
                <tr v-for="(row, index) in staff.list" :key="row.id || index" class="border-b border-gray-200">
                  <!-- 雇い入れメインタスクの問い合わせNo（メインタスクの登録時に req_akbs へ控えたもの。面接だけの申請と、控える前の申請は空）
                       小さく出し、押すとメインタスクかサブタスクかを選んで別タブで開く -->
                  <td class="px-3 py-2">
                    <button v-if="row.requestNo" type="button" class="no-link" @click="openTask(row.requestNo, row.employmentId || row.interviewsId, false)">{{ row.requestNo }}</button>
                  </td>
                  <!-- 種別（保存時に控えた type_name をそのまま出す。色は契約詳細の種別と同じ）。下に入退社日（新規入社・再入社は入社日、退職は退職日。それ以外は空） -->
                  <td class="px-3 py-2">
                    <div class="font-bold text-indigo-900">{{ row.typeName }}</div>
                    <div v-if="hireOrRetireDate(row)" class="sub-line">{{ hireOrRetireDate(row) }}</div>
                  </td>
                  <td class="px-3 py-2">{{ row.staff }}</td>
                  <!-- 作成者と、その下に承認者（承認したときに入る。どちらも xs） -->
                  <td class="px-3 py-2 text-xs">
                    <div>{{ row.creator }}</div>
                    <div v-if="row.approver">{{ row.approver }}</div>
                  </td>
                  <!-- 状況の下に日付（承認待ちは申請日、AKBS登録済みは承認日。それ以外は無し）。列は左右中央 -->
                  <td class="px-3 py-2 text-center">
                    <Badge :color="statusColor(row.status)">{{ row.status }}</Badge>
                    <div v-if="statusDate(row)" class="sub-line">{{ statusDate(row) }}</div>
                  </td>

                  <!-- 編集（入力画面を開く）。AKBS登録済みは入力画面が閲覧のみになるので「開く」と出す
                       見積リストの「参照」と同じ青い文字のリンク。承認の「確認」はボタンにして、開くだけの編集と見分けられるようにする -->
                  <!-- セル全体を押せるようにする（余白は td ではなくリンク側に持たせ、リンクをセルいっぱいに広げる） -->
                  <td class="p-0 text-center">
                    <router-link :to="{ name: 'AkbsInputForm', query: { id: row.id } }" class="open-link">
                      <template v-if="isRegistered(row)">
                        <Icon iconName="FolderOpen" class="w-4 h-4 mr-1" />
                        開く
                      </template>
                      <template v-else>
                        <Icon iconName="PencilAlt" class="w-4 h-4 mr-1" />
                        編集
                      </template>
                    </router-link>
                  </td>

                  <!-- 承認（AKBS権限があり承認待ちの行だけ押せる。見積リストの承認確認ボタンと同じ見た目） -->
                  <td class="px-3 py-2 text-center">
                    <!-- 承認のために開いたことが分かるよう approver を付けて遷移する（見積の承認確認と同じ形） -->
                    <router-link v-if="canApprove(row)" :to="{ name: 'AkbsInputForm', query: { id: row.id, approver: 'true' } }" class="flat-btn">
                      <Icon iconName="ClipboardCheck" class="w-4 h-4 mr-1" />
                      確認
                    </router-link>
                    <!-- 作成者本人は押せない（理由はツールチップ） -->
                    <span v-else class="flat-btn-disabled" v-tooltip="approveTip(row)">
                      <Icon iconName="ClipboardCheck" class="w-4 h-4 mr-1" />
                      確認
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- ページネーション（物件選択モーダルと同じ部品・見た目） -->
          <div class="my-2 w-fit mx-auto">
            <pagination id="pagenation-staff" v-model="staff.page" :per-page="perPage" :records="staff.records" class="pagenation text-center" @paginate="getStaffList()" />
          </div>
        </section>

        <!-- ========== 右：契約詳細の申請（変更処理から作った契約詳細変更・契約解約と、受注処理から作った契約詳細新規） ========== -->
        <section class="panel">
          <div class="panel-head">
            <div class="panel-title">
              <Icon iconName="OfficeBuilding" class="w-5 h-5 mr-1" />
              契約詳細の申請
              <span class="panel-count">{{ contract.records }} 件</span>
            </div>
            <!-- 絞り込み（得意先・対象物件・種別・作成者・状況） -->
            <div class="panel-filter">
              <input
                v-model="contract.filter.client"
                type="search"
                placeholder="得意先 検索"
                class="filter-input w-32 rounded border border-gray-300 px-2 text-sm"
                :class="contract.filter.client ? 'filled' : ''"
                @keydown.enter="getContractList(1)"
              />

              <input
                v-model="contract.filter.site"
                type="search"
                placeholder="物件名 検索"
                class="filter-input w-32 rounded border border-gray-300 px-2 text-sm"
                :class="contract.filter.site ? 'filled' : ''"
                @keydown.enter="getContractList(1)"
              />

              <!-- 種別（値は req_akbs_contract.type の数値。1：変更、2：解約。空は全て） -->
              <select v-model="contract.filter.type" class="filter-input rounded border-gray-300 text-sm" :class="contract.filter.type ? 'filled' : ''" title="種別">
                <option value="">種別：全て</option>
                <option v-for="type in contractTypeOptions" :key="type.code" :value="type.code">
                  {{ type.name }}
                </option>
              </select>

              <input
                v-model="contract.filter.creator"
                type="search"
                placeholder="作成者 検索"
                class="filter-input w-28 rounded border border-gray-300 px-2 text-sm"
                :class="contract.filter.creator ? 'filled' : ''"
                @keydown.enter="getContractList(1)"
              />

              <select v-model="contract.filter.status" class="filter-input rounded border-gray-300 text-sm" :class="contract.filter.status !== statusAll ? 'filled' : ''" title="状況">
                <option v-for="status in statusOptions" :key="status" :value="status">
                  {{ status === statusAll ? '状況：全て' : status }}
                </option>
              </select>

              <PrimaryButton text="検索" size="sm" class="w-20" @click="getContractList(1)" />
            </div>
          </div>

          <div class="list-box">
            <div v-if="contract.loading" class="flex h-full items-center justify-center font-bold text-gray-400">
              Searching...
            </div>

            <div v-else-if="!contract.list.length" class="flex h-full items-center justify-center font-bold text-gray-400">
              対象データがありません。
            </div>

            <table v-else class="w-full border-collapse text-sm whitespace-nowrap">
              <thead class="sticky top-0 bg-gray-100">
                <!-- 列を減らして2段にまとめる（種別の下に日付、得意先の下に対象物件、作成者の下に承認者、状況の下に申請日／承認日）。ユーザー 2026-09-22
                     「作成者」は左の一覧と同じ呼び方（一時保存だけの人も入るので「申請者」とは呼ばない） -->
                <tr class="border-b border-gray-300 text-left text-xs text-gray-600">
                  <th class="px-3 py-2">問い合わせNo</th>
                  <!-- 種別の下に日付だけ出す（変更なら変更日、解約なら解約日、新規なら足した契約コードの契約開始日のうち一番早い日） -->
                  <th class="px-3 py-2">種別</th>
                  <th class="px-3 py-2">得意先 / 対象物件</th>
                  <th class="px-3 py-2">作成 / 承認</th>
                  <th class="px-3 py-2 text-center">状況</th>
                  <th class="px-3 py-2 w-20 text-center">編集</th>
                  <th class="px-3 py-2 w-20 text-center">承認</th>
                </tr>
              </thead>
              <tbody class="text-gray-700">
                <tr v-for="(row, index) in contract.list" :key="row.id || index" class="border-b border-gray-200">
                  <!-- メインタスクの問い合わせNo（保存時に req_akbs_contract へ控えたもの。控える前の申請は、次の保存で入る）
                       押すとメインタスクかサブタスクかを選んで別タブで開く（契約詳細新規は受注処理、それ以外は変更処理） -->
                  <td class="px-3 py-2">
                    <button v-if="row.requestNo" type="button" class="no-link" @click="openTask(row.requestNo, row.orderId, isOrderType(row))">{{ row.requestNo }}</button>
                  </td>
                  <!-- 種別（種別の数値から短い名前で出す：変更・解約・新規。解約は赤字で目立たせる）。下に変更日／解約日／開始日 -->
                  <td class="px-3 py-2">
                    <div class="font-bold" :class="isEndType(row) ? 'text-red-700' : 'text-indigo-900'">{{ contractTypeName(row) }}</div>
                    <!-- 日付だけ（変更日・解約日・開始日のどれかは種別で分かるので、見出しは付けない） -->
                    <div v-if="row.changeDate" class="sub-line">{{ formatDate(row.changeDate) }}</div>
                  </td>
                  <!-- 得意先と、その下に対象物件（保存時に控えた site_names。5件まで、それ以上は「他○件」。長いので幅を抑えて省略する）。どちらも小さめ -->
                  <td class="px-3 py-2">
                    <div class="text-xs">{{ row.clientName }}</div>
                    <div class="sub-line site-names" :title="row.siteNames">{{ row.siteNames }}</div>
                  </td>
                  <!-- 作成者と、その下に承認者（どちらも xs） -->
                  <td class="px-3 py-2 text-xs">
                    <div>{{ row.creator }}</div>
                    <div v-if="row.approver">{{ row.approver }}</div>
                  </td>
                  <!-- 状況の下に日付（承認待ちは申請日、AKBS登録済みは承認日。それ以外は無し）。列は左右中央 -->
                  <td class="px-3 py-2 text-center">
                    <Badge :color="statusColor(row.status)">{{ row.status }}</Badge>
                    <div v-if="statusDate(row)" class="sub-line">{{ statusDate(row) }}</div>
                  </td>

                  <!-- 編集（契約詳細入力画面を開く）。清掃員の申請と同じ見た目 -->
                  <td class="p-0 text-center">
                    <router-link :to="{ name: 'AkbsContractForm', query: { id: row.id } }" class="open-link">
                      <template v-if="isRegistered(row)">
                        <Icon iconName="FolderOpen" class="w-4 h-4 mr-1" />
                        開く
                      </template>
                      <template v-else>
                        <Icon iconName="PencilAlt" class="w-4 h-4 mr-1" />
                        編集
                      </template>
                    </router-link>
                  </td>

                  <!-- 承認（契約詳細権限があり承認待ちの行だけ押せる。AKBS 権限とは別） -->
                  <td class="px-3 py-2 text-center">
                    <router-link v-if="canApprove(row, true)" :to="{ name: 'AkbsContractForm', query: { id: row.id, approver: 'true' } }" class="flat-btn">
                      <Icon iconName="ClipboardCheck" class="w-4 h-4 mr-1" />
                      確認
                    </router-link>
                    <!-- 作成者本人は押せない（理由はツールチップ） -->
                    <span v-else class="flat-btn-disabled" v-tooltip="approveTip(row, true)">
                      <Icon iconName="ClipboardCheck" class="w-4 h-4 mr-1" />
                      確認
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="my-2 w-fit mx-auto">
            <pagination id="pagenation-contract" v-model="contract.page" :per-page="perPage" :records="contract.records" class="pagenation text-center" @paginate="getContractList()" />
          </div>
        </section>
      </div>
    </main>
  </div>
</template>

<script>
import Icon from '@components/Icon.vue'
import Badge from '@components/Badge.vue'
import PageHeader from '@components/PageHeader.vue'
import PrimaryButton from '@components/PrimaryButton.vue'
import Pagination from 'vue-pagination-2'
import * as backend from '@libs/backend'
import * as dialogs from '@libs/dialogs'
import { AKBS_STATUS, AKBS_STATUS_COLOR, AKBS_TYPE_NAMES } from '@libs/constantsAKBS'
import PageModel from '../../main/pages/PageModel.vue'
import * as moment from 'moment'

// 状況の絞り込み（値の意味は constantsAKBS の AKBS_STATUS を参照）
// 「全て」はバックの getPendingList が状況の条件を付けずに検索する
// 削除した申請は一覧に出さない（バックが除く。ユーザー決定 2026-09-23。復活はメインタスク・サブタスクのボタンからだけ）ので、絞り込みにも無い
const STATUS_ALL = '全て'
const STATUS_OPTIONS = [STATUS_ALL, AKBS_STATUS.PENDING, AKBS_STATUS.SAVED, AKBS_STATUS.RETURNED, AKBS_STATUS.REGISTERED]
// 清掃員の申請だけ「未保存」がある（メインタスクを登録しただけの空の枠。契約詳細の申請には無い）
const STAFF_STATUS_OPTIONS = [STATUS_ALL, AKBS_STATUS.PENDING, AKBS_STATUS.UNSAVED, AKBS_STATUS.SAVED, AKBS_STATUS.RETURNED, AKBS_STATUS.REGISTERED]

// 契約詳細の申請の種別（req_akbs_contract.type の数値 → 表示名。契約詳細入力画面の TYPE_NAMES と同じ短い名前。ユーザー 2026-09-22）
// 2026-09-22 より前に保存した申請の type_name は「契約詳細変更・契約解約・契約詳細新規」のままなので、一覧は数値から名前を引く
// 1・2 は変更処理から、3 は受注処理から作る（新規登録）
const CONTRACT_TYPE_NAMES = { 1: '変更', 2: '解約', 3: '新規' }

// 絞り込みを控えるセッションストレージのキー（物件概要 SiteOutline と同じ仕組み）
// 左右で別々に控える（右は後から足したので別のキー）
const SESSION_STORAGE_NAME = 'akbsPendingListStorage'
const SESSION_STORAGE_NAME_CONTRACT = 'akbsPendingListContractStorage'

// 1ページの件数（左右とも同じ）
const PER_PAGE = 50

/**
 * 一覧1つ分の入れ物（左右で同じ形）。キーは v-model で後から足さず、ここで揃えておく
 * @param {Object} filter 絞り込みの初期値
 */
function createPanel(filter) {
  return {
    loading: false,
    list: [],
    // ページネーション（表示中のページ、絞り込み後の全件数）
    page: 1,
    records: 0,
    filter,
  }
}

export default {
  extends: PageModel,

  components: {
    Icon,
    Badge,
    PageHeader,
    PrimaryButton,
    Pagination,
  },

  data() {
    return {
      // AKBS 権限（清掃員の申請の承認）があるか（承認の「確認」ボタンを押せるかの判定。created で入れる）
      akbsAuth: false,
      // 契約詳細権限（契約詳細の申請の承認）があるか。AKBS 権限とは別（ユーザー決定 2026-10-09）
      contractDetailAuth: false,

      perPage: PER_PAGE,

      // 左：清掃員の申請（req_akbs）。絞り込みは清掃員名・種別・作成者・状況（状況の初期値は created で権限を見て決める）
      staff: createPanel({ status: STATUS_ALL, staff: '', type: '', creator: '' }),

      // 右：契約詳細の申請（req_akbs_contract）。絞り込みは得意先・物件名・種別・作成者・状況
      contract: createPanel({ status: STATUS_ALL, client: '', site: '', type: '', creator: '' }),

      statusOptions: STATUS_OPTIONS,
      staffStatusOptions: STAFF_STATUS_OPTIONS,
      // 状況の「全て」（テンプレートで絞っているかの判定に使う）
      statusAll: STATUS_ALL,
      // 種別の選択肢（数値と名前）
      staffTypeOptions: Object.keys(AKBS_TYPE_NAMES).map(code => ({ code, name: AKBS_TYPE_NAMES[code] })),
      contractTypeOptions: Object.keys(CONTRACT_TYPE_NAMES).map(code => ({ code, name: CONTRACT_TYPE_NAMES[code] })),
    }
  },

  created() {
    // 承認権限がある人は承認する申請を探しに来るので、その表の初期値を承認待ちにする。それ以外は全て
    // 清掃員の申請は AKBS 権限（akbsAuth）、契約詳細の申請は契約詳細権限（contractDetailAuth）
    const auth = this.$store.getters.auth
    this.akbsAuth = !!(auth && auth.akbsAuth)
    this.contractDetailAuth = !!(auth && auth.contractDetailAuth)
    if (this.akbsAuth) {
      this.staff.filter.status = AKBS_STATUS.PENDING
    }
    if (this.contractDetailAuth) {
      this.contract.filter.status = AKBS_STATUS.PENDING
    }
    // 前回検索した内容が残っていれば、権限で決めた初期値より優先する（入力画面から戻ったときに同じ絞り込みで開く）
    this.getSessionStorage()
    this.getAll()
  },

  methods: {
    /**
     * 左右の一覧を取り直す（今のページのまま）
     */
    getAll() {
      this.getStaffList()
      this.getContractList()
    },

    /**
     * 清掃員の申請の一覧を検索する
     * @param {Number} num 表示するページ（指定時はそのページへ移る。検索ボタンは1ページ目、ページの切り替えは指定なし）
     */
    async getStaffList(num) {
      // 種別は選んだ種別の数値を types で渡す（バックは「,」区切りの数値の並びを受ける。空なら絞らない）
      // API: akbsInputForm/getPendingList
      await this.getList(this.staff, 'akbsInputForm/getPendingList', num)
    },

    /**
     * 契約詳細の申請の一覧を検索する
     * @param {Number} num 表示するページ
     */
    async getContractList(num) {
      // API: akbsContractForm/getPendingList
      await this.getList(this.contract, 'akbsContractForm/getPendingList', num)
    },

    /**
     * 一覧を検索する（左右共通）
     * @param {Object} panel staff か contract
     * @param {String} url バックの API
     * @param {Number} num 表示するページ
     */
    async getList(panel, url, num) {
      panel.loading = true
      this.setSessionStorage()
      if (num) {
        panel.page = num
      }

      try {
        const { type, ...filter } = panel.filter
        const params = { ...filter, page: panel.page, perPage: this.perPage }
        if (type) {
          params.types = String(type)
        }

        const res = await backend.searchData(url, params)
        panel.list = res.data && Array.isArray(res.data.data) ? res.data.data : []
        // 全件数は各行の totalSize に入っている（0件なら行が無いので0）
        panel.records = panel.list.length ? Number(panel.list[0].totalSize) : 0
      } catch (error) {
        panel.list = []
        panel.records = 0
      } finally {
        panel.loading = false
      }
    },

    /**
     * 入退社日（種別が新規入社・再入社なら入社日、退職なら退職日。それ以外は空文字）
     * 値は req_akbs の入力内容から取っている（バックの getPendingList）
     * @param {Object} row 一覧の行
     */
    hireOrRetireDate(row) {
      const type = Number(row.type)
      if (type === 1 || type === 7) {
        return row.dateHire || ''
      }
      if (type === 6) {
        return row.dateResignation || ''
      }
      return ''
    },

    /**
     * 表示用の日付（yyyy/mm/dd。無ければ空）
     * @param {*} d 日付（文字列か Date）
     */
    formatDate(d) {
      if (!d) {
        return ''
      }
      const m = moment(d)
      return m.isValid() ? m.format('YYYY/MM/DD') : String(d)
    },

    /**
     * 契約詳細の申請が解約か（種別 2）
     * @param {Object} row 一覧の行
     */
    isEndType(row) {
      return Number(row.type) === 2
    },

    /**
     * 契約詳細の申請の種別の表示名（数値から。表に無ければ保存時の type_name）
     * @param {Object} row 一覧の行
     */
    contractTypeName(row) {
      return CONTRACT_TYPE_NAMES[Number(row.type)] || row.typeName || ''
    },

    /**
     * 契約詳細の申請が受注処理から作った新規登録か（種別 3。メインタスクは受注）
     * @param {Object} row 一覧の行
     */
    isOrderType(row) {
      return Number(row.type) === 3
    },

    /**
     * 問い合わせNo を押した：メインタスクかサブタスクかを選んで別タブで開く（一覧はそのまま残す）
     * @param {String} requestNo 問い合わせNo（ダイアログに出すだけ）
     * @param {String} mainId メインタスクの _id（雇用指示の申請は雇い入れ、無ければ面接。契約詳細の申請は order_id）
     * @param {Boolean} isOrder メインタスクが受注処理か（受注は RequestPost が mode 6＋orderId、サブタスク画面が mode 1＋orderId）
     */
    async openTask(requestNo, mainId, isOrder) {
      if (!mainId) {
        await dialogs.showErrorDialog('開けません', 'この申請にはメインタスクの控えがありません。')
        return
      }
      // ダイアログのボタンは右から 1・2・3 の順に並ぶ（MessageDialog が flex-row-reverse）。一番右をキャンセルにする
      const res = await dialogs.showConfirmDialog3('問い合わせNo ' + requestNo, 'どちらの画面を開きますか？（別タブで開きます）', 'キャンセル', 'サブタスク', 'メインタスク')
      let route = null
      if (res === '3') {
        route = isOrder
          ? { name: 'RequestPost', query: { mode: 6, orderId: mainId } }
          : { name: 'RequestPost', query: { mode: 1, requestPostId: mainId } }
      } else if (res === '2') {
        route = isOrder
          ? { name: 'TaskRequest', query: { orderId: mainId, mode: '1' } }
          : { name: 'TaskRequest', query: { requestPostId: mainId } }
      }
      if (route) {
        window.open(this.$router.resolve(route).href, '_blank')
      }
    },

    /**
     * 状況のバッジの色（表に無い状況は灰色）
     * @param {String} status 状況
     */
    statusColor(status) {
      return AKBS_STATUS_COLOR[status] || 'gray'
    },

    /**
     * 承認の「確認」ボタンを押せる行か（承認権限があり、承認待ちで、自分が作成者でないもの）
     * 作成者本人が承認すると申請と承認が同じ人になるので押せない（ユーザー指示）
     * @param {Object} row 一覧の行
     * @param {Boolean} isContract 契約詳細の申請の行か（契約詳細権限で見る。清掃員の申請は AKBS 権限）
     */
    canApprove(row, isContract) {
      const auth = isContract ? this.contractDetailAuth : this.akbsAuth
      return auth && row.status === AKBS_STATUS.PENDING && !this.isMyRequest(row)
    },

    /**
     * 自分が作成者の申請か（creator_id とログインユーザーの id を文字で比べる。req_akbs は bigint、req_akbs_contract は text のため）
     * @param {Object} row 一覧の行
     */
    isMyRequest(row) {
      const me = this.$store.getters.user && this.$store.getters.user.id
      return !!row.creatorId && !!me && String(row.creatorId) === String(me)
    },

    /**
     * 「確認」を押せないときの説明（承認待ちで自分の申請のときだけ）
     * @param {Object} row 一覧の行
     */
    approveTip(row, isContract) {
      const auth = isContract ? this.contractDetailAuth : this.akbsAuth
      if (auth && row.status === AKBS_STATUS.PENDING && this.isMyRequest(row)) {
        return '自分が作成した申請は承認できません。他の承認者に確認してもらってください。'
      }
      return ''
    },

    /**
     * 状況のバッジの下に出す日付（承認待ちは申請日、AKBS登録済みは承認日。それ以外は空。ユーザー 2026-09-22）
     * @param {Object} row 一覧の行
     */
    statusDate(row) {
      if (row.status === AKBS_STATUS.PENDING) {
        return this.formatDate(row.applicationDate)
      }
      if (row.status === AKBS_STATUS.REGISTERED) {
        return this.formatDate(row.approvedDate)
      }
      return ''
    },

    /**
     * AKBS登録済みの行か（入力画面は閲覧のみになるので「編集」ではなく「開く」と出す）
     * @param {Object} row 一覧の行
     */
    isRegistered(row) {
      return row.status === AKBS_STATUS.REGISTERED
    },

    /**
     * セッションストレージに控えた絞り込みを取得して filter に入れる（無ければ何もしない）
     * キーは data の filter のものを残し、控えにあるものだけ上書きする（後から足したキーがリアクティブにならないのを避ける）
     */
    getSessionStorage() {
      const staff = this.readStorage(SESSION_STORAGE_NAME)
      if (staff) {
        this.staff.filter = { ...this.staff.filter, ...staff }
      }
      const contract = this.readStorage(SESSION_STORAGE_NAME_CONTRACT)
      if (contract) {
        this.contract.filter = { ...this.contract.filter, ...contract }
      }
    },

    /**
     * セッションストレージの1件を読む（無い・壊れていれば null）
     * @param {String} name キー
     */
    readStorage(name) {
      try {
        return JSON.parse(sessionStorage.getItem(name))
      } catch (e) {
        return null
      }
    },

    /**
     * 絞り込みをセッションストレージに控える（検索のたびに保存する）
     */
    setSessionStorage() {
      sessionStorage.setItem(SESSION_STORAGE_NAME, JSON.stringify(this.staff.filter))
      sessionStorage.setItem(SESSION_STORAGE_NAME_CONTRACT, JSON.stringify(this.contract.filter))
    },
  },
}
</script>

<style scoped>
/* 左右のパネル。半分ずつ使い、中の一覧だけが残りの高さを使う。画面が狭いときは枠の中で横にスクロールする */
.panel {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* パネルの見出し（1段目：名前・件数、2段目：絞り込み）。左右で段数が揃うよう、常に2段にする */
.panel-head {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  padding: 6px 8px;
  border-radius: 6px 6px 0 0;
  background: #1e3a8a;
}

.panel-title {
  display: flex;
  align-items: center;
  color: #fff;
  font-weight: 700;
  font-size: 15px;
  white-space: nowrap;
}

.panel-count {
  margin-left: 12px;
  font-size: 18px;
}

.panel-filter {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

/* 一覧の枠。0件や検索中の文言が潰れないよう最小の高さだけ持つ */
.list-box {
  flex: 1 1 0;
  min-height: 120px;
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-top: none;
  border-radius: 0 0 6px 6px;
  background: #fff;
}

/* 対象物件は長いので幅を抑えて省略し、title で全部見せる */
.site-names {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* セルの2段目（入退社日・変更日・対象物件・申請日／承認日）。上の段より小さく、色は少しだけ薄く（薄すぎると読めない） */
.sub-line {
  font-size: 11px;
  line-height: 14px;
  color: #374151;
  margin-top: 1px;
}

/* 問い合わせNo（小さな青い文字。押すとメインタスク／サブタスクを選んで開く） */
.no-link {
  font-size: 11px;
  color: #1d4ed8;
  text-decoration: underline;
  white-space: nowrap;
}

.no-link:hover {
  color: #1e40af;
}

/* 絞り込みの入力欄は select と input で高さが違うので、同じ高さに揃える */
.filter-input {
  height: 32px;
  box-sizing: border-box;
}

/* ドロップダウンは @tailwindcss/forms の上下の余白（8px ずつ）が高さ 32px に入りきらず文字が欠けるので、余白を無くして行の高さで中央に置く */
select.filter-input {
  padding-top: 0;
  padding-bottom: 0;
  line-height: 30px;
}

/* 何かで絞っている欄（依頼一覧の絞り込みと同じ色） */
.filter-input.filled {
  font-weight: bold;
  background-color: #efdf8a;
}

/* 編集・開くのリンク（見積リストの「参照」と同じ青い文字。セルいっぱいに広げて押しやすくする） */
.open-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 12px;
  font-weight: 700;
  color: #2563eb;
}

.open-link:hover {
  color: #1e3a8a;
  text-decoration: underline;
  background: #eff6ff;
}

/* 編集・確認ボタン（見積リストの承認確認ボタン EstimateFlatButton と同じ見た目） */
.flat-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 14px;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
  background: #81aac7;
  color: #f0f7fd;
  border-bottom: 3px solid #667285;
  border-right: 3px solid #c0c0cb;
}

.flat-btn:hover {
  margin-top: 3px;
  color: #fff;
  border-bottom: 1px solid #667285;
  border-right: 1px solid #c0c0cb;
}

/* 押せないとき（権限が無い・承認待ちではない） */
.flat-btn-disabled {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 14px;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: #fff;
  background: rgba(156, 163, 175, 0.75);
}

/* ページネーション（物件選択モーダルと同じ見た目） */
.pagenation >>> ul {
  display: flex !important;
  color: rgb(6, 125, 194);
  font-weight: bold !important;
  cursor: pointer;
}

.pagenation >>> ul > li {
  padding: 0 10px;
}

.pagenation >>> ul > .active {
  background: rgb(6, 125, 194);
  color: white;
}

.pagenation >>> p {
  color: gray;
  font-size: small;
}
</style>
